import { Loader2 } from 'lucide-react'
import { testIds } from '@/lib/test-ids'
import type { CloudDispatchBannerModel } from '@/lib/cloud-dispatch'
import { openInboxThread } from '@/lib/cloud-dispatch'

export type CloudDispatchBannerProps = {
  dispatch: CloudDispatchBannerModel | null | undefined
  isDispatching?: boolean
  workspaceId?: string | null
  onWatchLive?: (agentId: string) => void
  className?: string
}

/**
 * Running / completed cloud agent strip (Product Board bet preview + document viewer).
 */
export function CloudDispatchBanner({
  dispatch,
  isDispatching = false,
  workspaceId,
  onWatchLive,
  className = '',
}: CloudDispatchBannerProps) {
  if (!isDispatching && !dispatch?.agentId && !dispatch?.error) return null

  return (
    <div
      className={`flex h-11 items-center border-b border-border bg-blue-500/5 px-4 ${className}`}
      data-testid={testIds.cloudDispatch.banner}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {isDispatching ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400 shrink-0" />
              <span className="text-blue-400 font-medium">Dispatching...</span>
            </>
          ) : dispatch?.error ? (
            <span className="text-red-400 font-medium text-xs truncate">{dispatch.error}</span>
          ) : dispatch?.agentId ? (
            <>
              <span className="text-blue-400 font-medium shrink-0">
                {dispatch.isActive !== false ? 'Cloud agent running' : 'Cloud agent finished'}
              </span>
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground truncate max-w-[140px]">
                {dispatch.agentId}
              </code>
            </>
          ) : null}
        </div>
        {!isDispatching && !dispatch?.error && dispatch?.agentId && (
          <div className="flex items-center gap-3 shrink-0">
            {dispatch.prUrl && (
              <a
                href={dispatch.prUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-400 hover:text-purple-300 font-medium underline"
                data-testid={testIds.cloudDispatch.viewMrLink}
              >
                View MR
              </a>
            )}
            {dispatch.threadId && (
              <button
                type="button"
                className="text-xs text-blue-400 hover:text-blue-300 underline"
                onClick={() =>
                  openInboxThread(dispatch.threadId as string, dispatch.title || 'Cloud agent', workspaceId)
                }
                data-testid={testIds.inbox.agentViewInInbox}
              >
                View in Inbox
              </button>
            )}
            {onWatchLive && dispatch.isActive !== false && dispatch.agentId && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => {
                  if (dispatch.agentId) onWatchLive(dispatch.agentId)
                }}
                data-testid={testIds.cloudDispatch.watchLiveButton}
              >
                Watch live
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
